// Copyright 2026 Bitech.id. Licensed under the MIT License.

import type { BiwaveClient } from '@biwave/core';

/**
 * Best-effort log/pulse flush when the page is being hidden/unloaded — not
 * guaranteed (a hard crash or the tab being killed can still lose whatever's
 * buffered). Browsers don't have process.on(), so this uses
 * visibilitychange/pagehide instead. client.flush() already covers both
 * buffers, so no Pulse-specific wiring is needed here.
 *
 * TODO: for the very last flush on unload, navigator.sendBeacon() would be
 * more reliable than fetch() (which browsers may cancel once the page is
 * gone) — not implemented in this pass.
 */
export function installLogFlushOnUnload(client: BiwaveClient): () => void {
  const flush = (): void => {
    void client.flush();
  };

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') flush();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', flush);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', flush);
  };
}
